import React, {createContext, useState, useEffect, useCallback, useMemo} from 'react';
import {
    Platform,
    View,
    Text,
    TouchableOpacity,
    StyleSheet,
    Modal,
} from 'react-native';

// Conditionally import Android-specific modules
const PermissionsAndroid = Platform.OS === 'android' ? require('react-native').PermissionsAndroid : null;
// Cross-platform toast: real ToastAndroid on Android, toast-message shim on iOS/web.
const ToastAndroid = require('./crossToast').default;
import AsyncStorage from '@react-native-async-storage/async-storage';
import {Buffer} from 'buffer';
import Feather from 'react-native-vector-icons/Feather';
import Toast from "react-native-toast-message";
import BluetoothClassic from 'react-native-bluetooth-classic';
import {useTranslation} from 'react-i18next';
import {isAppModuleEnabled} from '../config/appVariant';
import {ensureBluetoothScanPermissions} from './bluetoothPermissions';
import {safeJsonParse} from './safeJsonParse';

export const BluetoothContext = createContext();

// Bluetooth receipt printing (and the Android location permission it depends on)
// is only used by the sales, management and driver (receipt blokk) modules. The service-only variant must
// not prompt for location/Bluetooth permissions, so we gate the requests on those
// modules being enabled.
const areBluetoothPermissionsNeeded = () =>
    isAppModuleEnabled('pos') || isAppModuleEnabled('management') || isAppModuleEnabled('driver');

export const BluetoothProvider = ({children}) => {
    const {t} = useTranslation();
    const [devices, setDevices] = useState([]);
    const [connectedDevice, setConnectedDevice] = useState(null);
    const [isScanning, setIsScanning] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);

    // Request permissions (Android-specific)
    const requestPermissions = useCallback(async () => {
        if (Platform.OS === 'android') {
            // Service-only variants don't use Bluetooth printing and must not
            // request location/Bluetooth permissions.
            if (!areBluetoothPermissionsNeeded()) {
                return false;
            }
            try
            {
                //await BluetoothClassic.requestBluetoothEnabled();
                // Android 12+: BLUETOOTH_SCAN must be granted or the native
                // library's cancelDiscovery() (called on host destroy) throws a
                // SecurityException and crashes the app on exit.
                await ensureBluetoothScanPermissions();
                await PermissionsAndroid.request(PermissionsAndroid.PERMISSIONS.ACCESS_FINE_LOCATION);

                return true;
            } catch (error) {
                console.error('Error requesting permissions:', error);
                return false;
            }
        }
    }, []);


    useEffect(() => {
        const initializeDevice = async () => {

            // Skip Bluetooth/location permission prompts entirely for variants that
            // don't use Bluetooth printing (e.g. the service module).
            if (!areBluetoothPermissionsNeeded()) {
                return;
            }

            const permissionGranted = await requestPermissions();
            if (!permissionGranted) {
                // Bluetooth/location permissions are optional and only needed for
                // receipt printing. Silently skip device init instead of blocking
                // the user with a permission-denied alert on app start.
                return;
            }
            /*

            const storedDevice = await AsyncStorage.getItem('cashierDevice');
            if (storedDevice) {
                try {
                    const storedDeviceData = JSON.parse(storedDevice) || storedDevice;
                    await BluetoothClassic.connectToDevice(storedDeviceData.address);
                    const device = await BluetoothClassic.getConnectedDevice(storedDeviceData.address);
                    setConnectedDevice(device);
                } catch (error) {
                    console.error('Error initializing device:', {device: storedDevice, error: error});
                }
            }
             */
        };
        initializeDevice();
    }, [false]);


    const scanForDevices = useCallback(async () => {
        if (Platform.OS === 'android') {
            const permissionGranted = await requestPermissions();
            if (!permissionGranted) {
                // Permissions were denied — abort the scan quietly. The user
                // initiated this from the printer settings, so a Toast there
                // already communicates that no device could be found.
                return;
            }

            setIsScanning(true);
            setDevices([]);

            try {
                const availableDevices = await BluetoothClassic.list();
                setDevices(availableDevices);
                setModalVisible(true);
            } catch (error) {
                console.error('Error scanning for Bluetooth devices:', error);
            } finally {
                setIsScanning(false);
            }
        }
    }, [requestPermissions]);
    const pairDevice = async (device) => {
        try {
            await BluetoothClassic.connectToDevice(device.address);
            await AsyncStorage.setItem('cashierDevice', JSON.stringify(device));
            await AsyncStorage.setItem("selectedCashierDevice", JSON.stringify(device));
            Toast.show({type: 'success', text1: t('bluetooth_connected_to', {name: device.name})});
            setModalVisible(false);
        } catch (error) {
            console.error("Pairing error:", error);
            Toast.show({type: 'error', text1: t('bluetooth_connection_failed', {name: device.name}), text2: error.message});
        }
    };

    const sendData = useCallback(async (data) => {

        const storedDevice = await AsyncStorage.getItem('cashierDevice');
        if (!storedDevice || !storedDevice.trim()) {
            Toast.show({type: 'error', text1: t('no_connected_printer_title'), text2: t('no_connected_printer_message'), visibilityTime: 5000});
            return;
        }
        // The stored value can be a truncated/corrupted JSON string (e.g. app
        // killed mid-write) — an unguarded JSON.parse here throws "Unexpected
        // end of input" from inside a Bluetooth print callback (uncaught,
        // async, surfaces as a bare "tryCallOne" crash). safeJsonParse falls
        // back cleanly instead of throwing.
        const storedDeviceData = safeJsonParse(storedDevice, null) || storedDevice;
        if (!storedDeviceData || typeof storedDeviceData !== 'object') {
            Toast.show({type: 'error', text1: t('no_connected_printer_title'), text2: t('no_connected_printer_message'), visibilityTime: 5000});
            return;
        }

        const deviceAddress = storedDeviceData.address;

        try {
            const connection = await BluetoothClassic.connectToDevice(deviceAddress);
            await BluetoothClassic.writeToDevice(deviceAddress, data);
        } catch (error) {
            Toast.show({type: 'error', text1: t('receipt_print_error_device', {name: storedDeviceData.name}), text2: JSON.stringify(error), visibilityTime: 10000});
        }
    }, [t]);

    // Memoize the exposed value so the printer-modal state (modalVisible) toggling
    // doesn't re-render every BluetoothContext consumer. scanForDevices/sendData are
    // now useCallback-stable, so the ref changes only on real device/scan changes.
    const value = useMemo(() => ({
        devices,
        connectedDevice,
        isScanning,
        scanForDevices,
        sendData,
    }), [devices, connectedDevice, isScanning, scanForDevices, sendData]);

    return (
        <BluetoothContext.Provider value={value}>
            {children}
            <Modal
                animationType="slide"
                transparent={true}
                visible={modalVisible}
                onRequestClose={() => setModalVisible(false)}
            >
                <View style={styles.modalContainer}>
                    <View style={styles.modalContent}>
                        <Text style={styles.modalTitle}>{t('available_devices')}</Text>
                        {devices.map((device) => (
                            <View key={device.id} style={styles.deviceRow}>
                                <Feather name="printer" size={24} color="#000"/>
                                <View style={styles.deviceInfo}>
                                    <Text style={styles.deviceName}>{device.name}</Text>
                                    <Text style={styles.deviceMac}>{device.id}</Text>
                                </View>
                                <TouchableOpacity style={styles.connectButton} onPress={() => pairDevice(device)}>
                                    <Text style={styles.connectButtonText}>{t('connect')}</Text>
                                </TouchableOpacity>
                            </View>
                        ))}
                        <TouchableOpacity style={styles.closeButton} onPress={() => setModalVisible(false)}>
                            <Text style={styles.closeButtonText}>{t('close')}</Text>
                        </TouchableOpacity>
                    </View>
                </View>
            </Modal>
        </BluetoothContext.Provider>
    );
};

const styles = StyleSheet.create({
    modalContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
    },
    modalContent: {
        width: '80%',
        backgroundColor: 'white',
        borderRadius: 10,
        padding: 20,
        alignItems: 'center',
    },
    modalTitle: {
        fontSize: 20,
        fontWeight: 'bold',
        marginBottom: 20,
    },
    deviceRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 10,
        borderBottomWidth: 1,
        borderBottomColor: '#ccc',
        width: '100%',
    },
    deviceInfo: {
        flex: 1,
        marginLeft: 10,
    },
    deviceName: {
        fontSize: 16,
        fontWeight: 'bold',
    },
    deviceMac: {
        fontSize: 14,
        color: '#666',
    },
    connectButton: {
        backgroundColor: '#2196F3',
        borderRadius: 5,
        padding: 10,
    },
    connectButtonText: {
        color: 'white',
        fontWeight: 'bold',
    },
    closeButton: {
        marginTop: 20,
        backgroundColor: '#f44336',
        borderRadius: 5,
        padding: 10,
    },
    closeButtonText: {
        color: 'white',
        fontWeight: 'bold',
    },
});
